import { getRoomsBasePath, getRoomTabPath } from '../../service/constants';

export const MESSENGER_V2_GROUP_INVITE_ACCEPT_PATH = '/messages/groups/invite';
export const MESSENGER_V2_LEARN_MORE_PATH = '/end-of-time-chat';
export const MESSENGER_V2_INFO_MEMBER_QUERY = 'member';
export const MESSENGER_V2_CALL_PATH = '/calls';
const MOBILE_UI_PARAMS = Object.freeze({
  message: 'selected-message', chat: 'selected-chat', emoji: 'emoji', reaction: 'reaction-message',
});

export const resolveMessengerV2MobileUi = (search = '') => {
  const params = new URLSearchParams(search);
  const read = key => params.get(MOBILE_UI_PARAMS[key])?.trim() || null;
  const source = read('emoji');
  const reaction = read('reaction');
  return {
    message: read('message'), chat: read('chat'),
    emoji: source === 'composer' || (source === 'reaction' && reaction) ? source : null,
    reaction: source === 'reaction' ? reaction : null,
  };
};

export const buildMessengerV2MobileUiPath = (location, patch = {}) => {
  const params = new URLSearchParams(location.search || '');
  Object.entries(patch).forEach(([key, value]) => {
    const param = MOBILE_UI_PARAMS[key];
    if (!param) return;
    if (value == null || value === '') params.delete(param);
    else params.set(param, String(value));
  });
  if (params.get(MOBILE_UI_PARAMS.emoji) !== 'reaction') params.delete(MOBILE_UI_PARAMS.reaction);
  const search = params.toString();
  return `${location.pathname}${search ? `?${search}` : ''}${location.hash || ''}`;
};
export const MESSENGER_V2_SHOW_INBOX_NAVIGATION_STATE = Object.freeze({
  messengerV2ShowInbox: true,
});

export const shouldShowMessengerV2Inbox = (navigationState = null) =>
  navigationState?.messengerV2ShowInbox === true;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const resolveConversationKind = (conversation = {}) => {
  const kind = toStringOrNull(conversation?.kind ?? conversation?.meta?.kind ?? null);
  if (kind === 'room') return 'room';

  const groupId = toStringOrNull(
    conversation?.group_id ??
      conversation?.groupId ??
      conversation?.group?.id ??
      conversation?.group?.group_id ??
      conversation?.group?.groupId ??
      null,
  );
  return kind === 'group' || groupId ? 'group' : 'direct';
};

const resolveConversationRoomId = (conversation = {}) =>
  toStringOrNull(
    conversation?.room_id ??
      conversation?.roomId ??
      conversation?.room?.id ??
      conversation?.group_id ??
      conversation?.groupId ??
      conversation?.group?.id ??
      null,
  );

export const resolveMessengerV2RouteConversationId = (pathname = '') => {
  const normalizedPath = String(pathname || '');
  const groupMatch = normalizedPath.match(/^\/messages\/groups\/([^/]+)(?:\/|$)/);
  const groupId = toStringOrNull(groupMatch?.[1] ?? null);
  if (groupId) {
    const decodedGroupId = decodeURIComponent(groupId);
    return decodedGroupId === 'invite' ? null : decodedGroupId;
  }

  const directMatch = normalizedPath.match(/^\/messages\/([^/]+)(?:\/|$)/);
  const directId = toStringOrNull(directMatch?.[1] ?? null);
  if (!directId || directId === 'groups' || directId === 'group-invite') return null;
  return decodeURIComponent(directId);
};

export const isMessengerV2InboxPath = (pathname = '') =>
  /^\/messages\/?$/.test(String(pathname || ''));

export const isMessengerV2CallPath = (pathname = '') =>
  /^\/calls\/[^/]+\/?$/.test(String(pathname || '')) ||
  new RegExp(`^${getRoomsBasePath()}/[^/]+/calls/[^/]+/?$`).test(String(pathname || ''));

export const resolveMessengerV2CallRouteId = (pathname = '') => {
  const normalizedPath = String(pathname || '');
  const match =
    normalizedPath.match(/^\/calls\/([^/]+)\/?$/) ||
    normalizedPath.match(new RegExp(`^${getRoomsBasePath()}/[^/]+/calls/([^/]+)/?$`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

export const buildMessengerV2CallPath = (callId, returnPath = null) => {
  const normalizedCallId = toStringOrNull(callId);
  const normalizedReturnPath = toStringOrNull(returnPath);
  const roomReturnMatch = normalizedReturnPath?.match(
    new RegExp(`^${getRoomsBasePath()}/([^/?#]+)(?:/|$)`),
  );
  if (normalizedCallId && roomReturnMatch?.[1]) {
    return `${getRoomsBasePath()}/${roomReturnMatch[1]}/calls/${encodeURIComponent(
      normalizedCallId,
    )}`;
  }
  return normalizedCallId
    ? `${MESSENGER_V2_CALL_PATH}/${encodeURIComponent(normalizedCallId)}`
    : MESSENGER_V2_CALL_PATH;
};

export const buildMessengerV2ConversationPath = (conversationId, conversation = {}) => {
  if (resolveConversationKind(conversation) === 'room') {
    const roomId = resolveConversationRoomId(conversation);
    return roomId ? getRoomTabPath(roomId, 'members_chat') : '/messages';
  }

  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) return '/messages';
  const encodedConversationId = encodeURIComponent(normalizedConversationId);
  return resolveConversationKind(conversation) === 'group'
    ? `/messages/groups/${encodedConversationId}`
    : `/messages/${encodedConversationId}`;
};

export const buildMessengerV2ForwardPath = ({
  conversationId,
  conversation = {},
  messageId,
  attachmentIndex = null,
} = {}) => {
  const normalizedMessageId = toStringOrNull(messageId);
  const conversationPath = buildMessengerV2ConversationPath(conversationId, conversation);
  if (!normalizedMessageId) return conversationPath;
  const query =
    attachmentIndex == null
      ? ''
      : `?attachment=${Math.max(0, Math.trunc(Number(attachmentIndex) || 0))}`;
  return `${conversationPath}/forward/${encodeURIComponent(normalizedMessageId)}${query}`;
};

export const resolveMessengerV2ForwardRoute = (pathname = '', search = '') => {
  const normalizedPath = String(pathname || '');
  const match = normalizedPath.match(/^\/messages\/(groups\/)?([^/]+)\/forward\/([^/]+)\/?$/);
  if (!match) return null;
  const attachmentValue = new URLSearchParams(String(search || '')).get('attachment');
  const parsedAttachmentIndex = attachmentValue == null ? null : Number(attachmentValue);
  return {
    conversationId: decodeURIComponent(match[2]),
    messageId: decodeURIComponent(match[3]),
    isGroupRoute: Boolean(match[1]),
    attachmentIndex:
      parsedAttachmentIndex != null && Number.isFinite(parsedAttachmentIndex)
        ? Math.max(0, Math.trunc(parsedAttachmentIndex))
        : null,
  };
};

export const buildMessengerV2AttachmentComposerPath = (conversationId, conversation, draftId) => {
  const normalizedDraftId = toStringOrNull(draftId);
  const conversationPath = buildMessengerV2ConversationPath(conversationId, conversation);
  return normalizedDraftId
    ? `${conversationPath}/attachments/${encodeURIComponent(normalizedDraftId)}`
    : conversationPath;
};

export const buildMessengerV2ImageEditorPath = ({
  conversationId,
  conversation,
  draftId,
  tool = null,
  layerId = null,
} = {}) => {
  const composerPath = buildMessengerV2AttachmentComposerPath(
    conversationId,
    conversation,
    draftId,
  );
  if (!toStringOrNull(draftId)) return composerPath;
  const normalizedTool = toStringOrNull(tool);
  const normalizedLayerId = toStringOrNull(layerId);
  const toolPath = normalizedTool ? `/${encodeURIComponent(normalizedTool)}` : '';
  const layerPath = normalizedLayerId ? `/${encodeURIComponent(normalizedLayerId)}` : '';
  return `${composerPath}/edit${toolPath}${layerPath}`;
};

export const resolveMessengerV2AttachmentRoute = (pathname = '') => {
  const match = String(pathname || '').match(
    /^\/messages\/(groups\/)?([^/]+)\/attachments\/([^/]+)(?:\/edit(?:\/([^/]+)(?:\/([^/]+))?)?)?\/?$/,
  );
  if (!match) return null;
  const tool = toStringOrNull(match[4] ?? null);
  if (tool && !['crop', 'text', 'emoji', 'draw'].includes(tool)) return null;
  return {
    conversationId: decodeURIComponent(match[2]),
    isGroup: Boolean(match[1]),
    draftId: decodeURIComponent(match[3]),
    screen: String(pathname || '').includes('/edit') ? tool || 'edit' : 'composer',
    layerId: match[5] ? decodeURIComponent(match[5]) : null,
  };
};

export const buildMessengerV2MemberInfoPath = (infoPath, participantId) => {
  const normalizedPath = String(infoPath || '').trim();
  const normalizedParticipantId = String(participantId || '').trim();
  if (!normalizedPath || !normalizedParticipantId) return normalizedPath;
  const separator = normalizedPath.includes('?') ? '&' : '?';
  return `${normalizedPath}${separator}${MESSENGER_V2_INFO_MEMBER_QUERY}=${encodeURIComponent(
    normalizedParticipantId,
  )}`;
};

export const resolveMessengerV2InfoMemberId = (search = '') => {
  const params = new URLSearchParams(String(search || ''));
  const value = params.get(MESSENGER_V2_INFO_MEMBER_QUERY);
  return value ? value.trim() || null : null;
};

export const buildMessengerV2GroupInviteAcceptPath = (token = null) => {
  const normalizedToken = token == null ? '' : String(token).trim();
  const query = normalizedToken ? `?token=${encodeURIComponent(normalizedToken)}` : '';
  return `${MESSENGER_V2_GROUP_INVITE_ACCEPT_PATH}${query}`;
};

export const buildMessengerV2GroupInviteAcceptUrl = (token = null, origin = null) => {
  const normalizedOrigin =
    origin ||
    (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '');
  const path = buildMessengerV2GroupInviteAcceptPath(token);
  return normalizedOrigin ? new URL(path, normalizedOrigin).toString() : path;
};
